import type {RootEpic} from '../../root'
import {filter, map, mergeMap, switchMap, tap, throttleTime} from 'rxjs/operators'
import {isActionOf} from 'typesafe-actions'
import {historyActions, HistoryFilters} from './history-actions'
import {ID} from '../../../types/UtilityTypes'
import {apiRequests} from '../../../http/config'
import {EMPTY, Observable, of} from 'rxjs'
import {toastFailedResponse} from '../../../components/controls/Toaster/Toaster'
import type {
    HistoryAction,
    HistoryData,
    HistoryQuery,
    HistoryResources,
    HistoryResponse
} from '../../../responses/history'
import {flow, pipe, Predicate} from 'fp-ts/es6/function'
import * as A from 'fp-ts/Array'
import {diffAndPrepare, eqEntity, prepareForServer} from '../../../utils/entity-utils'
import {CommentData} from '../../../responses/comments'
import {combineEpics} from 'redux-observable'
import {none, some} from 'fp-ts/es6/Option'
import {historyRead} from '../../../utils/lenses'
import type {TypedAjaxResponse} from '../../../http/types'
import {CommentEditState} from './history-reducer'
import {getItem, setItem} from 'fp-ts-local-storage'
import * as IO from 'fp-ts/IO'
import * as O from 'fp-ts/Option'
import {toServerDate} from '../../../utils/date-utils'

const markSelfRead = (userId?: ID) => (data: HistoryData): HistoryData => data.user == userId ? historyRead.set(1)(data) : data

const filterActions = (f: (action: HistoryAction) => boolean) => flow(
    A.map((item: HistoryData) => {
        const actions = item.actions.filter(f)
        return A.isNonEmpty(actions) ? some<HistoryData>({
            ...item,
            actions,
        }) : none
    }),
    A.compact,
)

const LOG_KEY = 'comment-log'

interface CommentLog {
    action: 'comment_init' | 'comment_add' | 'comment_error',
    date: string,
    user_id: ID,
    task_id: ID,
    comment_id?: ID,
}

const pushLog = (log: CommentLog) => {
    pipe(
        getItem(LOG_KEY),
        IO.chain((data) => setItem(
            LOG_KEY,
            JSON.stringify(pipe(
                data,
                O.map(JSON.parse),
                O.getOrElseW(() => []),
                A.cons(log),
                A.takeLeft(200),
            ))
        )),
    )()
}

const send: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(historyActions.comment.request)),
    mergeMap(action => {
        const userId = state$.value.global.user.data?.id
        const filters: HistoryFilters = action.payload.filters
        const data: CommentData = action.payload.data

        return CommentEditState.match({
            update: ({comment}) => apiRequests.comment.update({
                data: diffAndPrepare<CommentData>(comment, data),
                params: {id: comment.id},
            })
        }, () => {
            switch (filters.type) {
                case 'project':
                    return apiRequests.project.comment({
                        data: prepareForServer(data),
                        params: {projectId: filters.projectId}
                    })
                case 'work-object':
                    return apiRequests.workObject.comment({
                        data: prepareForServer(data),
                        params: {objectId: filters.objectId}
                    })
                case 'task':
                    pushLog({
                        action: 'comment_init',
                        date: toServerDate(new Date()),
                        user_id: userId ?? 0,
                        task_id: filters.taskId,
                    })
                    return apiRequests.task.comment({
                        data: prepareForServer(data),
                        params: {taskId: filters.taskId}
                    }).pipe(
                        tap(res => {
                            if (res.response.success) {
                                pushLog({
                                    action: 'comment_add',
                                    date: toServerDate(new Date()),
                                    user_id: userId ?? 0,
                                    task_id: filters.taskId,
                                    comment_id: res.response.row.id,
                                })
                            } else {
                                pushLog({
                                    action: 'comment_error',
                                    date: toServerDate(new Date()),
                                    user_id: userId ?? 0,
                                    task_id: filters.taskId,
                                })
                            }
                        }),
                    )
            }
        })(state$.value.global.history.editState)
    }),
    map(({response}) => response.success ?
        historyActions.comment.success(response.row, 'user') :
        historyActions.comment.failure(toastFailedResponse(response, 'Отправка комментария не удалась'))
    )
)

const fetch: RootEpic = action$ => action$.pipe(
    filter(isActionOf(historyActions.fetch.request)),
    throttleTime(100),
    switchMap(action => {
        const kind = action.payload.filterType.indexOf('comments') !== -1 ? 'user' : undefined

        //const query: HistoryQuery = {type: action.payload.filterType}
        const query: HistoryQuery = {kind: kind}
        let request: Observable<TypedAjaxResponse<HistoryResponse>> = EMPTY
        switch (action.payload.type) {
            case 'project':
                request = apiRequests.history.project.list({
                    params: {projectId: action.payload.projectId},
                    query,
                })
                break
            case 'work-object':
                request = apiRequests.history.object.list({
                    params: {objectId: action.payload.objectId},
                    query,
                })
                break
            case 'task':
                request = apiRequests.history.task.list({
                    params: {taskId: action.payload.taskId},
                    query,
                })
                break
        }
        return request
    }),
    mergeMap(({response}) => of(response.success ?
        historyActions.fetch.success(response) :
        historyActions.fetch.failure(toastFailedResponse(response, 'Не удалось запросить комментарии')),
    )),
)

const socketUpdate: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(historyActions.socketUpdate)),
    mergeMap(({payload}) => {
        const {filters, data} = state$.value.global.history
        const userId = state$.value.global.user.data?.id
        const resources: HistoryResources = payload.resources
        let predicate: Predicate<HistoryAction> = () => true
        switch (filters.filterType.length) {
            case 1:
                switch (filters.filterType[0]) {
                    case 'comments':
                        predicate = action => action.key === 'comment'
                        break
                    case 'files':
                        predicate = action => action.key === 'file' || (action.key === 'comment' && action.values.every(id => resources.comment[id as ID]))
                        break
                }
                break
            case 2:
                predicate = action => action.key === 'file' || action.key === 'comment'
                break
        }
        const newData = A.union<HistoryData>(eqEntity)(data, pipe(
            payload.data,
            A.filter(data => data.type === filters.type),
            filterActions(predicate),
        ))
        if (data.length === newData.length) {
            return EMPTY
        }
        return of(historyActions.append({
            resources: payload.resources,
            data: payload.data.map(markSelfRead(userId)),
        }))
    }),
)

const remove: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(historyActions.delete.request)),
    mergeMap(() => {
        const editState = state$.value.global.history.editState
        return CommentEditState.is.delete(editState) ?
            apiRequests.comment.delete({params: {id: editState.comment.id}}) :
            EMPTY
    }),
    map(({response}) => response.success ?
        historyActions.delete.success(response.row) :
        historyActions.delete.failure(toastFailedResponse(response, 'Не удалось удалить комментарий'))
    ),
)

const deleteComment: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(historyActions.delete.success)),
    map(({payload}) => {
        const oldMessages = state$.value.global.history.data
        const data = oldMessages.filter(message => !(message.owner_type === 'comment' && message.owner_id === payload))
        const correction = data.length - oldMessages.length
        return historyActions.deleteComment({data, correction})
    })
)

const read: RootEpic = action$ => action$.pipe(
    filter(isActionOf(historyActions.read.request)),
    mergeMap(({payload}) => apiRequests.history.read({params: {messageId: payload}}).pipe(
        map(({response}) => response.success ?
            historyActions.read.success(payload) :
            historyActions.read.failure(toastFailedResponse(response, 'Не удалось прочитать комментарий')),
        ),
    )),
)

const readAll: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(historyActions.readAll.request)),
    mergeMap(() => {
        const filters = state$.value.global.history.filters
        switch (filters.type) {
            case 'project':
                return apiRequests.history.project.readAll({params: {projectId: filters.projectId}})
            case 'work-object':
                return apiRequests.history.object.readAll({params: {objectId: filters.objectId}})
            case 'task':
                return apiRequests.history.task.readAll({params: {taskId: filters.taskId}})
        }
    }),
    map(res => res.response.success ?
        historyActions.readAll.success(state$.value.global.history.filters) :
        historyActions.readAll.failure(toastFailedResponse(res.response, 'Не удалось отметить все комментарии прочитанными'))
    )
)

export const historyEpic = combineEpics(send, fetch, socketUpdate, remove, deleteComment, read, readAll)